import * as ng from 'angular';
import * as _ from 'underscore';

import { Experience, ExperienceTypeDef } from '../../';
import { EditorModel } from '../../editor/editor.model';
import { EditorPageModel } from '../../editor/editor-page.model';

import {WebRegPageType, registerStep} from './web-reg-pages';
import {GatewayRadiusModel} from '../gateway-radius.model';
import { EditorController } from '../../editor';

const settingsViewUrl = require('./web-reg-settings.html');
const headerViewUrl = require('./web-reg-page-header.html');
const footerViewUrl = require('./web-reg-page-footer.html');

ng.module('e360')
   .factory('webregEditorModelFactory', webregEditorModelFactory);

webregEditorModelFactory.$inject = ['instanceServiceFactory'];
function webregEditorModelFactory(instanceServiceFactory) {
   return instanceServiceFactory(WebRegEditorModel);
}

export interface WebRegEditorModelFactory {
   ({campaign, typeDef:ExperienceTypeDef, experience:Experience}):WebRegEditorModel
}

export class WebRegEditorModel extends EditorModel {
   readonly workspaceClass = 'device webreg';
   readonly settingsView = settingsViewUrl;
   private _pages:WebRegPageModel[];

   readonly headerViewUrl = headerViewUrl;
   readonly footerViewUrl = footerViewUrl;

   readonly ageRequirementType:string;
   readonly orgSlug:string;

   domainInfo:{
      loading:boolean,
      zone?:{
         status:string,
         name_servers:string[]
      }
   }

   private gatewayRadiusModel:GatewayRadiusModel;
   requireMediaUpload = false;

   static $inject = [
      'session',
      'l10n',
      'gettextCatalog',
      'showManageDomainModal',
      'showUrlGenerateModal',
      'experiencesService'
   // separating instance args from injected services
   ].concat(['campaign', 'typeDef', 'experience']);

   constructor(
      public session,
      private l10n,
      private gettextCatalog,
      private showManageDomainModal,
      private showUrlGenerateModal,
      private experiencesService,
      campaign, typeDef: ExperienceTypeDef, experience: Experience
   ) {
      super(campaign, typeDef, experience);

      this.hasSettings = true;
      this.hasPages = true;
      this.hasPreview = true;
      this.hasCode = true;
      this.orgSlug = this.session.getOrg().slug;

      // Adding this as a property for use on the view
      this.ageRequirementType = this.session.getCampaign().ageRequirementType;

      if (this.isNew) {
         let brandSettings = this.session.getBrand().settings.experience;

         let defaultSubdomain = campaign.name.toLowerCase().replace(/[^a-z0-9-]+/g, '');

         let defaults = {
            mirrorCampaignFooter: true,
            backgroundColor: brandSettings.backgroundColor,
            buttonTextColor: brandSettings.buttonTextColor,
            eventSelectLayout: 'list',
            font: brandSettings.mediaFont,
            fontSize: brandSettings.fontSize,
            fontColor: brandSettings.fontColor,
            footerBackgroundColor: brandSettings.footerBackgroundColor,
            footerFontSize: brandSettings.footerFontSize,
            footerFontColor: brandSettings.footerFontColor,
            footerHideDefaultLinks: brandSettings.footerHideDefaultLinks,
            footerLinkColor: brandSettings.footerLinkColor,
            footerLinkHoverColor: brandSettings.footerLinkHoverColor,
            gatewayRadiusMeters: 100,
            socialLinkColor: brandSettings.socialLinkColor,
            socialLinkHoverColor: brandSettings.socialLinkHoverColor,
            subdomain: defaultSubdomain,
            titleFont: brandSettings.mediaFont,
            titleFontColor: brandSettings.headerFontColor,
            titleFontSize: brandSettings.headerFontSize,
            linkColor: brandSettings.linkColor,
            linkHoverColor: brandSettings.linkHoverColor,
            userGateway: false,
            pages: [ registerStep ],
            uploads: { headerImage: brandSettings.uploads.genericHeader },
            termsURL: brandSettings.termsURL,
            privacyURL: brandSettings.privacyURL,
            cookiesURL: brandSettings.cookiesURL
         };

         const optInFields = ['contactOptin', 'contestOptin', 'promoOptin', 'salesOptin', 'termsAgree'];

         // on the first registration page on the Self Reg, we add the opt-in fields that are pre-defined in the Brand Settings
         let _row = 22;
         // avoid adding duplicate fields and/or apply new "opt-in" brand settings
         defaults.pages[0].fields = defaults.pages[0].fields.filter(f => f.type != 'optin');

         for (let optIn of optInFields) {
            if (brandSettings[optIn] && !ng.equals({}, brandSettings[optIn])) {
               const field = {
                  key: optIn,
                  type: 'optin',
                  // if present, the terms agreement field is always required
                  required: optIn == 'termsAgree',
                  sizeX: 1,
                  sizeY: 3,
                  minSizeX: 0,
                  minSizeY: 3,
                  row: _row,
                  col: 0,
                  description: brandSettings[optIn],
               }
               defaults.pages[0].fields.push(field);
               _row += 3;
            }
         }

         this.gettextCatalog.getString('Register');
         this.l10n.translateObject(
            defaults.pages[0],
            'titleText',
            'Register',
            this.languages
         );

         this.gettextCatalog.getString('Please provide the following information.');
         this.l10n.translateObject(
            defaults.pages[0].fields[0],
            'description',
            'Please provide the following information.',
            this.languages
         );

         this.gettextCatalog.getString('Your Details');
         this.l10n.translateObject(
            defaults.pages[0].fields[2],
            'description',
            'Your Details',
            this.languages
         );

         this.experience = {
            campaignId: campaign.id,
            type: typeDef.id,
            settings: ng.copy(defaults)
         };
      } else { // existing self-reg experience
         if (this.settings.domain) {
            this.loadDomainStatus();
         }
      }

      this.gatewayRadiusModel = new GatewayRadiusModel(this.settings.gatewayRadiusMeters, 1);

      this.gettextCatalog.getString('Self-Registration Site');
      this.l10n.translateObject(
         this.experience.settings,
         'pageTitle',
         'Self-Registration Site',
         this.languages
      );

      // Set per page content default settings
      this.gettextCatalog.getString('Confirm Age');
      this.l10n.translateObject(
         this.experience.settings,
         'ageGateTitle',
         'Confirm Age',
         this.languages
      );

      this.gettextCatalog.getString('Select Event');
      this.l10n.translateObject(
         this.experience.settings,
         'eventsTitle',
         'Select Event',
         this.languages
      );

      this.gettextCatalog.getString('Find Nearby Events');
      this.l10n.translateObject(
         this.experience.settings,
         'eventsTitleUserGateway',
         'Find Nearby Events',
         this.languages
      );

      this.gettextCatalog.getString('Below are the events in your area. Please click on a date and time that is convenient for you.');
      this.l10n.translateObject(
         this.experience.settings,
         'eventsText',
         'Below are the events in your area. Please click on a date and time that is convenient for you.',
         this.languages
      );

      this.gettextCatalog.getString('Registered');
      this.l10n.translateObject(
         this.experience.settings,
         'confirmationTitle',
         'Registered',
         this.languages
      );

      this.gettextCatalog.getString('Thank you for registering. We hope you enjoy the event!');
      this.l10n.translateObject(
         this.experience.settings,
         'confirmationText',
         'Thank you for registering. We hope you enjoy the event!',
         this.languages
      );

      // per client request, B-F to display "Location" in place of "Country"
      const countryTitleToTranslate = this.orgSlug.indexOf('brown-forman') >= 0 ? 'Location' : 'Country';
      this.l10n.translateObject(
         this.experience.settings,
         'countryTitle',
         countryTitleToTranslate,
         this.languages
      );

      this.gettextCatalog.getString('Photo Submission');
      this.l10n.translateObject(
         this.experience.settings,
         'mediaUploadTitle',
         'Photo Submission',
         this.languages
      );

      this.gettextCatalog.getString('Please upload a photo of your receipt to complete your contest entry.');
      this.l10n.translateObject(
         this.experience.settings,
         'mediaUploadText',
         'Please upload a photo of your receipt to complete your contest entry.',
         
         this.languages
      );
      this.createPages();
   }

   get pages() {
      return this._pages;
   }

   get gateway() {
      if (this.experience.settings.userGateway) {
         return 'user';
      }

      return 'none';
   }

   set gateway(gateway:string) {
      this.experience.settings.userGateway = gateway === 'user';
   }

   get gatewayRadius() {
      return this.gatewayRadiusModel.radius;
   }

   set gatewayRadius(radius:number) {
      this.gatewayRadiusModel.radius = radius;
      this.settings.gatewayRadiusMeters = this.gatewayRadiusModel.radiusMeters;
   }

   get previewUrl() {
      return `//${this.domain}`;
   }

   get domain() {
      if (this.settings.domain) {
         return this.settings.domain;
      } else {
         return `${this.settings.subdomain}.${this.session.selfRegRootDomain()}`;
      }
   }

   get countryPlaceholder() {
      // per client request, B-F to display "Location" in place of "Country"
      return (this.orgSlug.indexOf('brown-forman') >= 0) ? 
         this.l10n.getTranslatedString('-- Select Location --', this.language) : 
         this.l10n.getTranslatedString('-- Select Country --', this.language);
   }

   createPages() {
      // Adding pages like this and not to the experience model pages as
      // they are dummies and we don't want them in the saved model
      let modelPages = this.experience.settings.pages;
      let viewPages = _.map(modelPages, () => new WebRegPageModel(this));

      // Add dummy event select page at start
      viewPages.unshift(new EventSelectModel(this));

      // Add dummy age gate page to start (before event select) if campaign has age gate
      if (this.ageRequirementType) {
         viewPages.unshift(new AgeGateModel(this));
      }

      // Add dummy confirmation select page to end
      viewPages.push(new ConfirmationModel(this));

      this._pages = viewPages;

      this.onShowMediaUploadPage();
   }

   onAddPage() {
      let step = ng.copy(registerStep);
      step.fields = [];
      this.experience.settings.pages.push(step);

      // Simply add the page just before the confirmation page
      let index = this.pages.length - 1;
      this.pages.splice(index, 0, new WebRegPageModel(this));
   }

   deletePage(page:WebRegPageModel) {
      let pageIndex = this.pages.indexOf(page);

      // get offset between this.page array and experience.settings.pages array
      let offset = this.relativePagesOffset();

      this.experience.settings.pages.splice(pageIndex + offset, 1);
      this.pages.splice(pageIndex, 1);
   }

   sortPage(dragIndex:number, dropIndex:number) {
      // get offset between this.page array and experience.settings.pages array
      let offset = this.relativePagesOffset();

      let tmp = this.experience.settings.pages[dragIndex + offset];
      this.experience.settings.pages[dragIndex + offset] = this.experience.settings.pages[dropIndex + offset];
      this.experience.settings.pages[dropIndex + offset] = tmp;

      tmp = this.pages[dragIndex];
      this.pages[dragIndex] = this.pages[dropIndex];
      this.pages[dropIndex] = tmp;
   }

   /**
    * Returns an offset to make `this.pages` array indices relative to experience
    * pages array.
    *
    * The experience's pages array only contains form pages. Other pages like the
    * age gate page are dummy views and not added to this array.
    */
   relativePagesOffset() {
      let offset = - 1;// Offset by one for the event select page
      if (this.ageRequirementType) {
         // Offset by two for the event select page and the age gate page
         offset = - 2;
      }

      return offset;
   }

   private loadDomainStatus() {
      this.domainInfo = { loading:true };
      this.experiencesService.cloudFlareVerify(this.experience.id).then(res => {
         if (res.result.length > 0) {
            this.domainInfo.zone = res.result[0];
         }
         this.domainInfo.loading = false;
      });
   }

   checkNameservers() {
      this.manageDomain('nameservers');
   }

   manageDomain(stage:string = 'config') {
      let nameservers:string[];
      if (this.domainInfo) {
         nameservers = this.domainInfo.zone.name_servers;
      }

      this.showManageDomainModal(stage, this, nameservers).then(result => {
         this.settings.domain = result.domain;
         this.settings.subdomain = result.subdomain;
      });
   }

   showUrlBuilder() {
      this.showUrlGenerateModal(this.settings.allowGuestTypeInURL, this.domain, false);
   }

   onShowMediaUploadPage(editorCtrl:EditorController = null) {
      if (this.settings.requireMediaUpload) {
         const insertIndex = Math.max(this.pages.length - 1, 0);
         const mediaUploaderPage = new MediaUploaderModel(this);
         this.pages.splice(insertIndex, 0, mediaUploaderPage);
      } else {
         if (editorCtrl) {
            // if the active page is the Media Uploader page, switchto first page
            if (editorCtrl.active instanceof MediaUploaderModel) {
               editorCtrl.setActive(this.pages[0], 0);
            }
            this.pages.splice(this.pages.length - 2, 1);
         }
      }
   }
}

/* ================ Web Registration Page Model ================ */

const pageRegistration = require('./web-reg-registration.html');

export class WebRegPageModel extends EditorPageModel {
   readonly view = pageRegistration;
   type = WebRegPageType.Registration;// Used for showing page settings

   constructor(editor:WebRegEditorModel) {
      super(editor);
   }

   get sortable() {
      return this.type == WebRegPageType.Registration;
   }

   get settings() {
      // Must get offset index as the indices of the pages don't match the indices of the steps
      let offset = (this.editor as WebRegEditorModel).relativePagesOffset();
      let index = this.editor.pages.indexOf(this);

      return this.editor.settings.pages[index + offset];
   }
}

const pageAgeGate = require('./web-reg-age-gate.html');

class AgeGateModel extends WebRegPageModel {
   readonly view = pageAgeGate;
   readonly type = WebRegPageType.AgeGate;

   constructor(editor:WebRegEditorModel) {
      super(editor);
   }

   get settings() {
      return null;// Don't try to load settings for dummy pages
   }
}

const pageEventSelect = require('./web-reg-event-select.html');

class EventSelectModel extends WebRegPageModel {
   readonly view = pageEventSelect;
   readonly type = WebRegPageType.EventSelect;

   constructor(editor:WebRegEditorModel) {
      super(editor);
   }

   get settings() {
      return null;
   }
}

const pageConfirmation = require('./web-reg-confirmation.html');

class ConfirmationModel extends WebRegPageModel {
   readonly view = pageConfirmation;
   readonly type = WebRegPageType.Confirmation;

   constructor(editor:WebRegEditorModel) {
      super(editor);
   }

   get settings() {
      return null;
   }
}

const pageMediaUploader = require('./web-reg-media-uploader.html');

class MediaUploaderModel extends WebRegPageModel {
   readonly view = pageMediaUploader;
   readonly type = WebRegPageType.MediaUploader;

   constructor(editor:WebRegEditorModel) {
      super(editor);
   }

   get settings() {
      return null;
   }
}
